import React, { useEffect, useState, useRef } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  toggleChat,
  closeChat,
  createSessionRequest,
  sendMessageRequest,
  fetchMessagesRequest,
  connectWebSocketRequest
} from 'src/activities/chat/chatAction';
import { IChatState } from 'src/interface/IChatState';
import { detectLanguage } from 'src/utils/languageDetector';
import { getPageContext } from 'src/utils/pageContextDetector';
import ChatButton from './ChatButton';
import ChatErrorBoundary from './ChatErrorBoundary';
import ChatWindow from './ChatWindow';

interface IRootState {
  chat: IChatState;
}

export const ChatWidget: React.FC = () => {
  const { i18n } = useTranslation();
  const dispatch = useDispatch();
  const router = useRouter();

  const {
    isOpen,
    unreadCount,
    activeSessionId,
    sessions,
    messages,
    loading,
    typing,
    websocketConnected,
    preferences
  } = useSelector((state: IRootState) => state.chat);

  // Track current page context
  const [currentPageContext, setCurrentPageContext] = useState(() => getPageContext());
  const prevPathRef = useRef(router.pathname);

  // Initialize chat on mount
  useEffect(() => {
    // Check if user has existing session
    if (sessions.length === 0 && !activeSessionId) {
      // Create new session on first open
      if (isOpen) {
        const language = i18n.language || preferences?.language || 'vi';
        dispatch(createSessionRequest(language));
      }
    } else if (activeSessionId && !websocketConnected) {
      // Connect WebSocket for active session
      dispatch(connectWebSocketRequest(activeSessionId));
    }
  }, [isOpen, sessions, activeSessionId, websocketConnected, preferences, i18n.language, dispatch]);

  // Update page context on route change
  useEffect(() => {
    if (router.pathname !== prevPathRef.current) {
      prevPathRef.current = router.pathname;
      setCurrentPageContext(getPageContext());
    }
  }, [router.pathname]);

  // Fetch messages when session becomes active
  useEffect(() => {
    if (activeSessionId && isOpen) {
      const sessionMessages = messages[activeSessionId];
      if (!sessionMessages || sessionMessages.length === 0) {
        dispatch(fetchMessagesRequest(activeSessionId));
      }
    }
    // Only re-run when activeSessionId or isOpen changes, not when messages changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSessionId, isOpen, dispatch]);

  const handleToggleChat = () => {
    dispatch(toggleChat());
  };

  const handleCloseChat = () => {
    dispatch(closeChat());
  };

  const handleSendMessage = (content: string) => {
    if (!activeSessionId) {
      // Create session first if none exists
      const language = i18n.language || preferences?.language || 'vi';
      dispatch(createSessionRequest(language));
      // Message will be sent after session is created via saga
      return;
    }

    // Detect language from user message or use current i18n language
    const detectedLanguage = detectLanguage(content);
    const language = detectedLanguage || i18n.language || preferences?.language || 'vi';

    // Use tracked page context (updated on route changes)
    dispatch(sendMessageRequest(activeSessionId, content, language, currentPageContext));
  };

  // Get messages for active session
  const activeMessages = activeSessionId ? messages[activeSessionId] || [] : [];

  return (
    <ChatErrorBoundary>
      <ChatButton onClick={handleToggleChat} unreadCount={unreadCount} />
      <ChatWindow
        isOpen={isOpen}
        onClose={handleCloseChat}
        messages={activeMessages}
        onSendMessage={handleSendMessage}
        loading={loading}
        typing={typing}
        websocketConnected={websocketConnected}
      />
    </ChatErrorBoundary>
  );
};

export default ChatWidget;
